import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from './client.js';
import {
  generationLifecycleRefetchInterval,
  hasActiveGeneration,
  invalidateGenerationReadModels,
  mergeAcceptedGeneration,
} from './generation-lifecycle.js';
import { queryKeys } from './query-keys.js';
import {
  createDeliveryRequest,
  createCreationPreset,
  createGeneration,
  createOwnedPlaybackSession,
  createPlaylist,
  createProject,
  createPreviewSession,
  deletePlaylist,
  deleteProject,
  ensureLibraryTrack,
  getCreationCapabilities,
  getDeliveryView,
  getFavoriteCommand,
  getCatalogRemovalCommand,
  getGenerationView,
  getLibraryImport,
  getPlaylistItems,
  getPlaylistView,
  getProject,
  getOpeningImpactAudit,
  getTrackDetail,
  getWebSession,
  listActivity,
  listCreationPresets,
  listDestinations,
  listLibraryTrackCards,
  listManagedLibraries,
  listPlaylistOptions,
  listPlaylists,
  listAllProjects,
  listAllProjectCards,
  listGenerations,
  listProviderConnections,
  listSynchronizations,
  listTrackCards,
  replacePlaylistItems,
  replaceTrackMetadata,
  requestSynchronization,
  requestCatalogRemoval,
  requestOpeningImpactAudit,
  restoreCatalogTrack,
  setFavorite,
  signIn,
  signOut,
  updatePlaylist,
  updateProject,
  type ActivityQuery,
  type AllProjectsQuery,
  type AllProjectCardsQuery,
  type CreateDeliveryBody,
  type CreateCreationPresetBody,
  type CreateGenerationBody,
  type CreatePlaylistBody,
  type CreateProjectBody,
  type DeliveryViewQuery,
  type FavoriteBody,
  type GenerationViewQuery,
  type GenerationQuery,
  type GenerationPage,
  type LibraryTrackCardQuery,
  type MetadataBody,
  type PlaylistOptionsQuery,
  type PlaylistQuery,
  type ReplacePlaylistItemsBody,
  type SynchronizationBody,
  type TrackCardQuery,
  type TrackDetailQuery,
  type UpdatePlaylistBody,
  type UpdateProjectBody,
} from './resources.js';

const visibleAndOnline = () =>
  (typeof document === 'undefined' || document.visibilityState === 'visible') &&
  (typeof navigator === 'undefined' || navigator.onLine);

export function shouldRetrySession(failureCount: number, error: unknown) {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) return error.status !== 401 && error.retryable;
  return true;
}

export function useSessionQuery() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: getWebSession,
    retry: shouldRetrySession,
    retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 4_000),
    retryOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
}

export function useSignInMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      signIn(credentials.email, credentials.password),
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session, session);
    },
  });
}

export function useSignOutMutation() {
  return useMutation({ mutationFn: signOut });
}

export function useWorkspaceDiscovery(enabled = true) {
  const connections = useQuery({
    queryKey: queryKeys.connections(),
    queryFn: () => listProviderConnections(),
    enabled,
  });
  const libraries = useQuery({
    queryKey: queryKeys.libraries(),
    queryFn: () => listManagedLibraries(),
    enabled,
  });
  const connection =
    connections.data?.items.find((item) => item.status === 'active') ?? connections.data?.items[0];
  const library =
    libraries.data?.items.find((item) => item.status === 'active') ?? libraries.data?.items[0];
  return { connections, libraries, connection, library };
}

export function useCreationCapabilitiesQuery(connectionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.capabilities(connectionId ?? 'unresolved'),
    queryFn: () => getCreationCapabilities(connectionId!),
    enabled: connectionId !== undefined,
  });
}

export function useCreationPresetsQuery(connectionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.presets(connectionId ?? 'unresolved'),
    queryFn: () => listCreationPresets(connectionId!),
    enabled: connectionId !== undefined,
  });
}

export function useCreateCreationPresetMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intent: { body: CreateCreationPresetBody; idempotencyKey: string }) =>
      createCreationPreset(intent.body, intent.idempotencyKey),
    onSuccess: async (_preset, intent) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.presets(intent.body.connection_id),
      });
    },
  });
}

export function useCreateGenerationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intent: { body: CreateGenerationBody; idempotencyKey: string }) =>
      createGeneration(intent.body, intent.idempotencyKey),
    onSuccess: async (generation, intent) => {
      const lifecycleKey = queryKeys.generationLifecycle(intent.body.connection_id);
      queryClient.setQueryData<GenerationPage>(lifecycleKey, (page) =>
        mergeAcceptedGeneration(page, generation),
      );
      await queryClient.invalidateQueries({ queryKey: lifecycleKey });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['generations'] }),
        invalidateGenerationReadModels(queryClient),
      ]);
    },
  });
}

export function useGenerationLifecycleQuery(connectionId: string) {
  return useQuery({
    queryKey: queryKeys.generationLifecycle(connectionId),
    queryFn: () => listGenerations({ limit: 100 }),
    refetchInterval: ({ state }) =>
      generationLifecycleRefetchInterval(state.data, visibleAndOnline()),
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
}

export function useProjectsQuery(query: AllProjectsQuery = { limit: 100 }) {
  return useQuery({
    queryKey: queryKeys.projects(query),
    queryFn: () => listAllProjects(query),
  });
}

export function useProjectCardsQuery(query: AllProjectCardsQuery) {
  return useQuery({
    queryKey: queryKeys.projectCards(query),
    queryFn: () => listAllProjectCards(query),
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
}

export function useProjectQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.project(projectId ?? 'unresolved'),
    queryFn: () => getProject(projectId!),
    enabled: projectId !== undefined,
  });
}

export function useOpeningImpactAuditQuery(
  projectId: string | undefined,
  connectionId: string | undefined,
  libraryId: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.openingImpactAudit(
      projectId ?? 'unresolved',
      connectionId ?? 'unresolved',
      libraryId ?? 'unresolved',
    ),
    queryFn: () => getOpeningImpactAudit(projectId!, connectionId!, libraryId!),
    enabled: projectId !== undefined && connectionId !== undefined && libraryId !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      const status = state.data?.audit?.status;
      return status === 'queued' || status === 'running' ? 2_000 : false;
    },
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
}

export function useRequestOpeningImpactAuditMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intent: {
      projectId: string;
      connectionId: string;
      libraryId: string;
      idempotencyKey: string;
    }) =>
      requestOpeningImpactAudit(
        intent.projectId,
        intent.connectionId,
        intent.libraryId,
        intent.idempotencyKey,
      ),
    onSuccess: (audit) => {
      queryClient.setQueryData(
        queryKeys.openingImpactAudit(
          audit.composer_project_id,
          audit.provider_connection_id,
          audit.managed_library_id,
        ),
        { audit },
      );
    },
  });
}

export function useProjectGenerationsQuery(query: GenerationQuery | undefined) {
  return useInfiniteQuery({
    queryKey: queryKeys.generations(query ?? {}),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listGenerations({ ...query!, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: query !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      return state.data?.pages.some(hasActiveGeneration) ? 3_000 : false;
    },
  });
}

export function useCreateProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intent: { body: CreateProjectBody; idempotencyKey: string }) =>
      createProject(intent.body, intent.idempotencyKey),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['project-cards'] }),
      ]);
    },
  });
}

export function useUpdateProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intent: { projectId: string; body: UpdateProjectBody }) =>
      updateProject(intent.projectId, intent.body),
    onSuccess: async (project) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['project-cards'] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.project(project.id) }),
      ]);
    },
  });
}

export function useDeleteProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intent: { projectId: string; expectedRevision: number }) =>
      deleteProject(intent.projectId, intent.expectedRevision),
    onSuccess: async (result) => {
      queryClient.removeQueries({ queryKey: queryKeys.project(result.project_id) });
      for (const generationId of result.deleted_generation_ids) {
        queryClient.removeQueries({ queryKey: ['generation', generationId] });
      }
      for (const trackId of result.deleted_track_ids) {
        queryClient.removeQueries({ queryKey: ['track', trackId] });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['project-cards'] }),
        queryClient.invalidateQueries({ queryKey: ['generations'] }),
        queryClient.invalidateQueries({ queryKey: ['catalog'] }),
        queryClient.invalidateQueries({ queryKey: ['library-tracks'] }),
        queryClient.invalidateQueries({ queryKey: ['playlists'] }),
        queryClient.invalidateQueries({ queryKey: ['playlist'] }),
        queryClient.invalidateQueries({ queryKey: ['playlist-items'] }),
        queryClient.invalidateQueries({ queryKey: ['activity'] }),
      ]);
    },
  });
}

export function useGenerationViewQuery(
  generationId: string | undefined,
  query: GenerationViewQuery | undefined,
) {
  return useQuery({
    queryKey: queryKeys.generation(
      generationId ?? 'unresolved',
      query ?? { library_id: 'unresolved' },
    ),
    queryFn: () => getGenerationView(generationId!, query!),
    enabled: generationId !== undefined && query !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      const status = state.data?.generation.status;
      return status && ['queued', 'submitting', 'submitted', 'processing'].includes(status)
        ? 3_000
        : false;
    },
  });
}

export function useCatalogTrackCardsQuery(query: TrackCardQuery | undefined) {
  return useQuery({
    queryKey: queryKeys.catalog(query ?? { connection_id: 'unresolved', library_id: 'unresolved' }),
    queryFn: () => listTrackCards(query!),
    enabled: query !== undefined,
  });
}

export function useLibraryTrackCardsQuery(
  libraryId: string | undefined,
  query: LibraryTrackCardQuery = {},
) {
  return useQuery({
    queryKey: queryKeys.libraryTracks(libraryId ?? 'unresolved', query),
    queryFn: () => listLibraryTrackCards(libraryId!, query),
    enabled: libraryId !== undefined,
  });
}

export function useTrackDetailQuery(
  trackId: string | undefined,
  query: TrackDetailQuery | undefined,
) {
  return useQuery({
    queryKey: queryKeys.track(
      trackId ?? 'unresolved',
      query ?? { connection_id: 'unresolved', library_id: 'unresolved' },
    ),
    queryFn: () => getTrackDetail(trackId!, query!),
    enabled: trackId !== undefined && query !== undefined,
  });
}

export function useFavoriteCommandQuery(commandId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.favoriteCommand(commandId ?? 'unresolved'),
    queryFn: () => getFavoriteCommand(commandId!),
    enabled: commandId !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      const status = state.data?.status;
      return status && ['queued', 'applying'].includes(status) ? 2_000 : false;
    },
  });
}

export function useFavoriteMutation() {
  return useMutation({
    mutationFn: (intent: { trackId: string; body: FavoriteBody; idempotencyKey: string }) =>
      setFavorite(intent.trackId, intent.body, intent.idempotencyKey),
  });
}

export function useCatalogRemovalMutation() {
  return useMutation({
    mutationFn: (intent: {
      libraryId: string;
      connectionId: string;
      trackIds: readonly string[];
      idempotencyKey: string;
    }) =>
      requestCatalogRemoval(
        intent.libraryId,
        { connection_id: intent.connectionId, track_ids: [...intent.trackIds] },
        intent.idempotencyKey,
      ),
  });
}

export function useCatalogRemovalCommandQuery(commandId: string | undefined) {
  return useQuery({
    queryKey: ['catalog-removal-command', commandId ?? 'unresolved'],
    queryFn: () => getCatalogRemovalCommand(commandId!),
    enabled: commandId !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      const status = state.data?.status;
      return status && ['queued', 'processing'].includes(status) ? 2_000 : false;
    },
  });
}

export function useRestoreCatalogTrackMutation() {
  return useMutation({
    mutationFn: (intent: { libraryId: string; trackId: string }) =>
      restoreCatalogTrack(intent.libraryId, intent.trackId),
  });
}

export function useEnsureLibraryTrackMutation() {
  return useMutation({
    mutationFn: (intent: { libraryId: string; trackId: string }) =>
      ensureLibraryTrack(intent.libraryId, intent.trackId),
  });
}

export function useLibraryImportQuery(libraryId: string | undefined, importId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.import(libraryId ?? 'unresolved', importId ?? 'unresolved'),
    queryFn: () => getLibraryImport(libraryId!, importId!),
    enabled: libraryId !== undefined && importId !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      const status = state.data?.status;
      return status && ['pending', 'materializing'].includes(status) ? 5_000 : false;
    },
  });
}

export function usePlaylistsQuery(libraryId: string | undefined, query: PlaylistQuery = {}) {
  return useQuery({
    queryKey: queryKeys.playlists(libraryId ?? 'unresolved', query),
    queryFn: () => listPlaylists(libraryId!, query),
    enabled: libraryId !== undefined,
  });
}

export function usePlaylistViewQuery(
  libraryId: string | undefined,
  playlistId: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.playlist(libraryId ?? 'unresolved', playlistId ?? 'unresolved'),
    queryFn: () => getPlaylistView(libraryId!, playlistId!),
    enabled: libraryId !== undefined && playlistId !== undefined,
  });
}

export function usePlaylistItemsQuery(
  libraryId: string | undefined,
  playlistId: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.playlistItems(libraryId ?? 'unresolved', playlistId ?? 'unresolved'),
    queryFn: () => getPlaylistItems(libraryId!, playlistId!),
    enabled: libraryId !== undefined && playlistId !== undefined,
  });
}

export function usePlaylistOptionsQuery(
  libraryId: string | undefined,
  trackId: string | undefined,
  query: PlaylistOptionsQuery = {},
) {
  return useQuery({
    queryKey: queryKeys.playlistOptions(libraryId ?? 'unresolved', trackId ?? 'unresolved', query),
    queryFn: () => listPlaylistOptions(libraryId!, trackId!, query),
    enabled: libraryId !== undefined && trackId !== undefined,
  });
}

export function useCreatePlaylistMutation() {
  return useMutation({
    mutationFn: (intent: { libraryId: string; body: CreatePlaylistBody; idempotencyKey: string }) =>
      createPlaylist(intent.libraryId, intent.body, intent.idempotencyKey),
  });
}

export function useUpdatePlaylistMutation() {
  return useMutation({
    mutationFn: (intent: { libraryId: string; playlistId: string; body: UpdatePlaylistBody }) =>
      updatePlaylist(intent.libraryId, intent.playlistId, intent.body),
  });
}

export function useDeletePlaylistMutation() {
  return useMutation({
    mutationFn: (intent: { libraryId: string; playlistId: string }) =>
      deletePlaylist(intent.libraryId, intent.playlistId),
  });
}

export function useReplacePlaylistItemsMutation() {
  return useMutation({
    mutationFn: (intent: {
      libraryId: string;
      playlistId: string;
      body: ReplacePlaylistItemsBody;
    }) => replacePlaylistItems(intent.libraryId, intent.playlistId, intent.body),
  });
}

export function useReplaceTrackMetadataMutation() {
  return useMutation({
    mutationFn: (intent: { libraryId: string; trackId: string; body: MetadataBody }) =>
      replaceTrackMetadata(intent.libraryId, intent.trackId, intent.body),
  });
}

export function useActivityQuery(query: ActivityQuery = {}) {
  return useQuery({
    queryKey: queryKeys.activity(query),
    queryFn: () => listActivity(query),
    refetchInterval: () => (visibleAndOnline() ? 15_000 : false),
  });
}

export function useSettingsDiscovery(connectionId: string | undefined) {
  const connections = useQuery({
    queryKey: queryKeys.connections(),
    queryFn: () => listProviderConnections(),
  });
  const libraries = useQuery({
    queryKey: queryKeys.libraries(),
    queryFn: () => listManagedLibraries(),
  });
  const destinations = useQuery({
    queryKey: queryKeys.destinations(),
    queryFn: () => listDestinations(),
  });
  const synchronizations = useSynchronizationsQuery(connectionId);
  return { connections, libraries, destinations, synchronizations };
}

export function useSynchronizationsQuery(connectionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.synchronizations(connectionId ?? 'unresolved'),
    queryFn: () => listSynchronizations(connectionId!),
    enabled: connectionId !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      return state.data?.items.some((item) =>
        ['queued', 'running', 'retryable'].includes(item.status),
      )
        ? 5_000
        : false;
    },
  });
}

export function useRequestSynchronizationMutation() {
  return useMutation({
    mutationFn: (intent: {
      connectionId: string;
      body: SynchronizationBody;
      idempotencyKey: string;
    }) => requestSynchronization(intent.connectionId, intent.body, intent.idempotencyKey),
  });
}

export function useCreateDeliveryMutation() {
  return useMutation({
    mutationFn: (intent: { body: CreateDeliveryBody; idempotencyKey: string }) =>
      createDeliveryRequest(intent.body, intent.idempotencyKey),
  });
}

export function useDeliveryViewQuery(
  requestId: string | undefined,
  query: DeliveryViewQuery | undefined,
) {
  return useQuery({
    queryKey: queryKeys.delivery(requestId ?? 'unresolved', query ?? { library_id: 'unresolved' }),
    queryFn: () => getDeliveryView(requestId!, query!),
    enabled: requestId !== undefined && query !== undefined,
    refetchInterval: ({ state }) => {
      if (!visibleAndOnline()) return false;
      const status = state.data?.request.status;
      return status && ['pending', 'expanding', 'in_progress'].includes(status) ? 5_000 : false;
    },
  });
}

export function usePreviewPlaybackMutation() {
  return useMutation({ mutationFn: (trackId: string) => createPreviewSession(trackId) });
}

export function useOwnedPlaybackMutation() {
  return useMutation({
    mutationFn: (intent: { libraryId: string; importId: string }) =>
      createOwnedPlaybackSession(intent.libraryId, intent.importId),
  });
}
